/**
 * Roteiro geral — a viagem inteira, um dia por linha, no layout da referência
 * do PO (teste físico, item 28).
 *
 * As linhas são DERIVADAS das datas da viagem: uma viagem de 3 dias produz 3
 * linhas, uma de 22 produz 22. O conteúdo de cada linha vem dos dias **desta**
 * viagem, no servidor — nunca de um conjunto de exemplo.
 *
 * Cada dia pode ter um tema (Parques, Compras, Show…) e uma cor: a faixa à
 * esquerda e o pino do tema são a cor do dia. Os filtros no alto — Todos ·
 * Planejados · Livres · um por tema em uso — só escondem linhas; um dia sem
 * atividade diz "Sem programação" e é o estado normal, não uma falha.
 */
import {
  buildItineraryDays,
  DAY_CATEGORY_LABELS,
  dayColorOf,
  dayPeriod,
  type DayCategory,
  featureEnabled,
  formatDayMonth,
  pluralize,
  resolveToday,
} from '@nv/core';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAgora } from '../../demo/DemoContext';
import { useDiasDaViagem } from '../../trip/dias';
import { useTrip } from '../../trip/TripContext';
import { tripInputDaViagem } from '../../trip/tripInput';
import { Icon } from '../../ui/icons';
import { Ilustracao } from '../../ui/Ilustracao';
import { ErrorState, Skeleton } from '../../ui/primitives';
import { useAniversariosDaViagem } from '../aniversarios/api';

type Filtro = 'todos' | 'planejados' | 'livres' | DayCategory;

export function RoteiroScreen() {
  const { trip, tripId, members } = useTrip();
  const viagem = tripInputDaViagem(trip, members.length);
  const now = useAgora(viagem);
  const dias = useDiasDaViagem(tripId);
  const aniversarios = useAniversariosDaViagem(tripId, featureEnabled(trip.features, 'aniversarios'));
  const aniversarioEm = new Map<string, string[]>();
  for (const b of aniversarios.data?.birthdays ?? []) aniversarioEm.set(b.date, [...(aniversarioEm.get(b.date) ?? []), b.name]);
  const [filtro, setFiltro] = useState<Filtro>('todos');

  if (!viagem) {
    return (
      <section className="screen">
        <ErrorState
          title="Não foi possível abrir o roteiro"
          description="Os dados desta viagem vieram incompletos."
          onRetry={() => window.location.reload()}
        />
      </section>
    );
  }


  const hoje = (() => {
    try {
      return resolveToday(viagem.timeZone, now, viagem.utcOffsetSchedule);
    } catch {
      return null;
    }
  })();

  const linhas = buildItineraryDays(viagem, hoje);
  const conteudo = new Map((dias.days ?? []).map((d) => [d.date, d]));

  const planejados = linhas.filter((l) => (conteudo.get(l.date)?.activityCount ?? 0) > 0 || conteudo.get(l.date)?.title).length;
  const livres = linhas.length - planejados;
  const temasEmUso = [...new Set((dias.days ?? []).map((d) => d.category).filter((c): c is DayCategory => Boolean(c)))];

  const visiveis = linhas.filter((l) => {
    const info = conteudo.get(l.date);
    const planejado = (info?.activityCount ?? 0) > 0 || Boolean(info?.title);
    if (filtro === 'todos') return true;
    if (filtro === 'planejados') return planejado;
    if (filtro === 'livres') return !planejado;
    return info?.category === filtro;
  });

  return (
    <section className="screen rot">
      <header className="rot-header">
        <div>
          <h1 className="rot-title">Roteiro</h1>
        </div>
        <Link className="rot-mapa" to="/mapa" aria-label="Mapa">
          <Icon name="mapa" size={20} />
        </Link>
      </header>

      <div className="rot-filtros" role="tablist" aria-label="Filtrar dias" data-testid="rot-filtros">
        {(
          [
            ['todos', 'Todos'],
            ['planejados', 'Planejados'],
            ['livres', 'Livres'],
            ...temasEmUso.map((c) => [c, DAY_CATEGORY_LABELS[c].rotulo] as const),
          ] as [Filtro, string][]
        ).map(([chave, rotulo]) => (
          <button
            type="button"
            key={chave}
            role="tab"
            aria-selected={filtro === chave}
            className={filtro === chave ? 'rot-filtro rot-filtro--ativo' : 'rot-filtro'}
            onClick={() => setFiltro(chave)}
            data-testid={`rot-filtro-${chave}`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {!dias.carregando && (
        <div className="rot-stats" data-testid="rot-stats">
          <span className="rot-stat">
            <span className="rot-stat__icone rot-stat__icone--brand"><Icon name="roteiro" size={18} /></span>
            <span className="rot-stat__texto"><strong>{planejados}</strong> {planejados === 1 ? 'dia planejado' : 'dias planejados'}</span>
          </span>
          <span className="rot-stat">
            <span className="rot-stat__icone"><Icon name="hospedagem" size={18} /></span>
            <span className="rot-stat__texto"><strong>{livres}</strong> {livres === 1 ? 'livre' : 'livres'}</span>
          </span>
          <Link className="rot-stat rot-stat--link" to="/grupo" aria-label="Ver o grupo">
            <span className="rot-stat__icone"><Icon name="pessoas" size={18} /></span>
            <span className="rot-stat__texto"><strong>{members.length}</strong> {members.length === 1 ? 'pessoa' : 'pessoas'}</span>
          </Link>
        </div>
      )}

      {dias.carregando && (
        <>
          <Skeleton height={64} />
          <Skeleton height={80} count={5} gap={10} />
        </>
      )}

      {!dias.carregando && (
        <div className="rot-list">
          {visiveis.map((dia) => {
            const info = conteudo.get(dia.date);
            const atividades = info?.activityCount ?? 0;
            const vazio = atividades === 0 && !info?.title;
            const cor = info ? dayColorOf(info.category ?? null, info.color ?? null) : null;
            const periodo = dayPeriod(info?.startTimes ?? []);
            const detalhe = [periodo, info?.destination].filter(Boolean);

            return (
              <Link
                key={dia.date}
                to={`/roteiro/${dia.date}`}
                className={`rot-row${dia.isToday ? ' rot-row--today' : ''}${vazio ? ' rot-row--vazio' : ''}${cor ? ` rot-row--${cor}` : ''}`}
                aria-current={dia.isToday ? 'date' : undefined}
                data-testid="rot-dia"
                data-tema={info?.category ?? ''}
              >
                {/* Refino final: a faixa lateral virou um bloco de data na cor do dia. */}
                <span className="rot-row__data">
                  {/* "hoje" no lugar do dia da semana: é a informação que importa. */}
                  <span className="rot-row__weekday">
                    {dia.isToday ? 'hoje' : dia.weekday.slice(0, 3).toLowerCase()}
                  </span>
                  <span className="rot-row__date">{formatDayMonth(dia.date)}</span>
                </span>
                {vazio && <Ilustracao nome="diaLivre" tamanho={40} className="rot-row__ilustracao" />}
                <span className="rot-row__body">
                  <span className={vazio ? 'rot-row__title rot-row__title--vazio' : 'rot-row__title'}>
                    {info?.title ??
                      (atividades === 0
                        ? 'Sem programação'
                        : pluralize(atividades, 'atividade', 'atividades'))}
                  </span>
                  {info?.category && (
                    <span className="rot-row__tema" data-testid="rot-tema">{DAY_CATEGORY_LABELS[info.category].rotulo}</span>
                  )}
                  {/* Dia livre não ganha frase de enfeite (PO, 17/09/2026). */}
                  {!vazio && (
                    detalhe.length > 0 && (
                      <span className="rot-row__detalhe">
                        {periodo && (
                          <>
                            <Icon name="horario" size={12} /> {periodo}
                          </>
                        )}
                        {periodo && info?.destination ? ' · ' : ''}
                        {info?.destination && (
                          <>
                            <Icon name="local" size={12} /> {info.destination}
                          </>
                        )}
                      </span>
                    )
                  )}
                  {aniversarioEm.has(dia.date) && (
                    <span className="rot-row__aniv" data-testid="rot-aniversario">
                      <Icon name="bolo" size={14} /> aniversário de {aniversarioEm.get(dia.date)!.join(' e ')}
                    </span>
                  )}
                </span>
                {atividades > 0 ? (
                  <span className="rot-chip">{atividades}</span>
                ) : (
                  <span className="rot-row__criar" aria-hidden="true">
                    <Icon name="criar" size={18} />
                  </span>
                )}
              </Link>
            );
          })}
          {visiveis.length === 0 && (
            <p className="rot-nota">Nenhum dia com este filtro.</p>
          )}
        </div>
      )}
    </section>
  );
}
